// Buscas da tela do Pós-venda (#6503). A regra de cada número está em
// src/lib/visaoGeral/posVenda.ts.
import { useEffect, useState } from 'react';
import { supabase } from '../../src/lib/supabase';
import { embedToArray } from '../../src/lib/utils';
import {
  ACOES_DECISAO, ACOES_REINICIO,
  type NegociacaoDaFila, type RegistroDaAnalise,
} from '../../src/lib/visaoGeral/posVenda';
import type { Agrupamento } from '../../src/lib/visaoGeral/comercial';
import { carregarReprovacoes } from './useGestaoComercial';

const PAGINA = 1000;
const LOTE = 200;

async function paginar<T>(montar: () => any): Promise<T[]> {
  const todas: T[] = [];
  for (let p = 0; ; p++) {
    const { data, error } = await montar().range(p * PAGINA, p * PAGINA + PAGINA - 1);
    if (error) throw error;
    todas.push(...((data || []) as T[]));
    if (!data || data.length < PAGINA) break;
  }
  return todas;
}

/** Registros das negociações em lotes (o `in` de 1.000 ids não cabe na URL). */
async function registrosDe(ids: number[], acoes: readonly string[]): Promise<RegistroDaAnalise[]> {
  const todos: RegistroDaAnalise[] = [];
  for (let i = 0; i < ids.length; i += LOTE) {
    todos.push(...await paginar<RegistroDaAnalise>(() => supabase.from('negociacao_logs')
      .select('id, negociacao_id, acao, detalhes, created_at, user_id')
      .in('negociacao_id', ids.slice(i, i + LOTE))
      .in('acao', acoes as string[])
      .order('id')));
  }
  return todos;
}

function useBusca<T>(buscar: () => Promise<T>, deps: unknown[], rotulo: string): { dados: T | null; erro: boolean } {
  const [estado, setEstado] = useState<{ dados: T | null; erro: boolean }>({ dados: null, erro: false });
  useEffect(() => {
    let vivo = true;
    setEstado({ dados: null, erro: false });
    buscar()
      .then(dados => { if (vivo) setEstado({ dados, erro: false }); })
      .catch(e => { console.error(`${rotulo}:`, e); if (vivo) setEstado({ dados: null, erro: true }); });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return estado;
}

/** Negociações na fila da análise (termo assinado e em análise) e os reinícios de cada uma. */
export const useFilaDaAnalise = () => useBusca<{ negociacoes: NegociacaoDaFila[]; registros: RegistroDaAnalise[] }>(async () => {
  const linhas = await paginar<any>(() => supabase.from('negociacoes')
    .select('id, cliente_nome, consumo_medio_kwh, status, data_assinatura_termo, analise_cadastral_minutos_realizados, analise_cadastral(decisao_final, decisao_final_at)')
    .in('status', ['termo_assinado', 'termo_analise'])
    .order('id'));
  const negociacoes: NegociacaoDaFila[] = linhas.map(l => {
    const ultima = embedToArray(l.analise_cadastral)
      .sort((a: any, b: any) => String(b.decisao_final_at ?? '').localeCompare(String(a.decisao_final_at ?? '')))[0] as any;
    return {
      id: Number(l.id), cliente_nome: l.cliente_nome, consumo_medio_kwh: l.consumo_medio_kwh, status: l.status,
      data_assinatura_termo: l.data_assinatura_termo, decisao_final: ultima?.decisao_final ?? null, decisao_final_at: ultima?.decisao_final_at ?? null,
      analise_cadastral_minutos_realizados: l.analise_cadastral_minutos_realizados ?? null,
    };
  });
  const registros = await registrosDe(negociacoes.map(n => n.id), ACOES_REINICIO);
  return { negociacoes, registros };
}, [], 'Fila da análise');

/**
 * Decisões da análise no período e o histórico das negociações decididas (decisões e
 * reinícios de qualquer época), mais a assinatura de cada uma.
 */
export const useDecisoesDoPeriodo = (inicio: string, fim: string) => useBusca<{ registros: RegistroDaAnalise[]; assinaturas: Map<number, string | null> }>(async () => {
  const doMes = await paginar<{ negociacao_id: number }>(() => supabase.from('negociacao_logs')
    .select('id, negociacao_id')
    .in('acao', [...ACOES_DECISAO])
    .gte('created_at', inicio)
    .lt('created_at', fim)
    .order('id'));
  const ids = [...new Set(doMes.map(d => Number(d.negociacao_id)))];
  if (ids.length === 0) return { registros: [], assinaturas: new Map() };
  const [registros, negs] = await Promise.all([
    registrosDe(ids, [...ACOES_DECISAO, ...ACOES_REINICIO]),
    (async () => {
      const todas: { id: number; data_assinatura_termo: string | null }[] = [];
      for (let i = 0; i < ids.length; i += LOTE) {
        const { data, error } = await supabase.from('negociacoes').select('id, data_assinatura_termo').in('id', ids.slice(i, i + LOTE));
        if (error) throw error;
        todas.push(...((data || []) as any[]));
      }
      return todas;
    })(),
  ]);
  return { registros, assinaturas: new Map(negs.map(n => [Number(n.id), n.data_assinatura_termo])) };
}, [inicio, fim], 'Decisões do mês');

/** Motivos das reprovações da análise no período (mesma conta da Gestão Comercial). */
export const useReprovacoesDoPeriodo = (inicio: string, fim: string) =>
  useBusca<Agrupamento>(() => carregarReprovacoes(inicio, fim), [inicio, fim], 'Reprovações do mês');
